import React, { useCallback, useEffect } from "react";
import { Route, Switch } from "react-router-dom";
import client from "@culturehq/client";
import { Loader } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch } from "app/hooks";
import { getProfilePath } from "app/links";
import { seo, t } from "app/utils";

import NotFound from "components/NotFound";

import ProfileSettings from "components/profile/ProfileSettings";
import PersonLeftInfo from "components/profile/PersonLeftInfo";
import Options from "components/person/Options";
import PointHistory from "components/person/PointHistory";
import UserActivity from "components/person/UserActivity";
import UserConnections from "components/person/UserConnections";
import getUserName from "app/utils/getUserName";
import StoriesCreated from "./person/StoriesCreated";

const PersonRoutes = ({ baseUrl, fetching = false, isProfile, onChange, match, user }) => (
  <div className="Person Container-left_nav">
    <div className="Left-nav no-scrollbar Person--Information SidePanel col-xs-12">
      <section role="complementary" aria-label={t("Profile left bar")}>
        <Loader loading={fetching}>
          <PersonLeftInfo onChange={onChange} user={user} />
        </Loader>
      </section>
    </div>
    <div className="Container-left_nav--main-column col-xs-12">
      <section role="main">
        <Loader loading={fetching}>
          <div className="clearfix col-xs-12">
            <Options user={user} isProfile={isProfile} />
            <Switch>
              <Route
                exact
                path={`${baseUrl}/achievements/:page?`}
                render={() => <PointHistory user={user} />}
              />
              <Route exact path={`${baseUrl}/activity/posts`} render={() => <UserActivity match={match} />} />
              <Route exact path={`${baseUrl}/activity/events/:page?`} render={() => <UserActivity match={match} />} />
              <Route exact path={`${baseUrl}/activity/recognitions/:page?`} render={() => <UserActivity match={match} />} />
              <Route exact path={`${baseUrl}/community/following`} render={() => <UserConnections match={match} />} />
              <Route exact path={`${baseUrl}/community/followers`} render={() => <UserConnections match={match} />} />
              <Route exact path={`${baseUrl}/settings/emails`} render={() => <ProfileSettings match={match} />} />
              <Route exact path={`${baseUrl}/settings/interests`} render={() => <ProfileSettings match={match} />} />
              <Route exact path={`${baseUrl}/settings/password`} render={() => <ProfileSettings match={match} />} />
              <Route exact path={`${baseUrl}/settings/skills`} render={() => <ProfileSettings match={match} />} />
              <Route exact path={`${baseUrl}/settings/clubs`} render={() => <ProfileSettings match={match} />} />
              <Route exact path={`${baseUrl}/settings/social`} render={() => <ProfileSettings match={match} />} />
              <Route exact path={`${baseUrl}/settings`} render={() => <ProfileSettings match={match} />} />
              <Route
                exact
                path={baseUrl}
                render={() => <StoriesCreated user={user} isProfile={isProfile} />}
              />
              <Route
                exact
                path={`${baseUrl}/new/stories/:id`}
                render={() => <StoriesCreated user={user} isProfile={isProfile} />}
              />
              <Route
                exact
                path={`${baseUrl}/new/stories/create`}
                render={() => <StoriesCreated user={user} isProfile={isProfile} />}
              />
            </Switch>
          </div>
        </Loader>
      </section>
    </div>
  </div>
);

const PersonUser = ({ match, userId }) => {
  const { error, fetching, json: { user = {} }, onUpdate } = useFetch(
    () => client.getUser({ userId }),
    [userId]
  );

  useEffect(
    () => {
      seo({
        title: `${getUserName(user)} - CultureHQ.com`
      });
    },
    [user, user.name]
  );

  const handleChange = useCallback(
    update => onUpdate(() => ({ user: update })),
    [onUpdate]
  );

  if (error && error.status === 404) {
    return <NotFound />;
  }

  if (error) {
    throw error;
  }

  return (
    <PersonRoutes
      baseUrl={getProfilePath(user)}
      fetching={fetching}
      isProfile={false}
      match={match}
      onChange={handleChange}
      user={user}
    />
  );
};

const Person = ({ match }) => {
  const { profile } = useProfile();

  if (match.params.user) {
    return <PersonUser match={match} userId={match.params.user} />;
  }

  return <PersonRoutes baseUrl="/profile" isProfile match={match} user={profile} />;
};

export default Person;
